import { AnimatedHeight } from "@/atoms-react/animated-height/AnimatedHeight.react";
import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import InputLabel from "@/atoms-react/input/Input.react";
import type { LayoutBaseProps } from "@/atoms-react/layout-base/LayoutBase.react";
import type { DeclarativeOccupation } from "@/features/flow/types";
import { useUser } from "@/features/user/hooks/use-user";
import type { NaturalPerson } from "@/features/user/types.ts";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { useAppDispatch } from "@/redux/hooks";
import { setIsInPreviousStep } from "@/redux/slices/flowSlice";
import { isCSPCodeNoOccupation, isDeclarativeOccupationValid } from "@/utils/declarative.utils";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import _ from "lodash";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { FlowHeader } from "../../../../flow-header.react";
interface DeclarativeIdentityActivityProps extends LayoutBaseProps {
  onValidated?: (output: DeclarativeIdentityActivityOutput) => void;
  profile?: NaturalPerson;
  declarativeOccupation?: DeclarativeOccupation;
}

export type DeclarativeIdentityActivityOutput = DeclarativeOccupation;

function DeclarativeIdentityOccupation({
  onValidated = () => {},
  profile,
  declarativeOccupation
}: DeclarativeIdentityActivityProps) {
  const { t } = useTranslation(["pass"]);
  const { locale } = useLocale();
  const { useCSP, useActivities } = useUser();
  const { data: CSPCodes } = useCSP(locale.language);
  const { data: activities } = useActivities(locale.language);
  const dispatch = useAppDispatch();

  const [data, setData] = useState<DeclarativeOccupation>({
    activity: declarativeOccupation?.activity
      ? declarativeOccupation?.activity
      : (profile?.ActivityCode?.toString() ?? null),
    CSPcode: declarativeOccupation?.CSPcode
      ? declarativeOccupation?.CSPcode
      : (profile?.CSPCode ?? null),
    description: declarativeOccupation?.description ?? null
  });

  function execute() {
    if (!isDeclarativeOccupationValid(data)) {
      toast.error(t("flow.id_control.error.must_enter_fields"));
      return;
    }
    dispatch(setIsInPreviousStep(false));
    onValidated(data);
  }

  const isActivityVisible = !!data.CSPcode && !isCSPCodeNoOccupation(data.CSPcode);
  const isActivitySelected = data.activity !== null && data.activity !== undefined;
  const isContinueDisabled =
    isActivityVisible &&
    (data.description === null || data.description.length < 20 || data.description.length > 160);

  const activityDescriptionError = () => {
    if (data.activity === null || data.activity === undefined) return undefined;
    if (!data.description) return t("flow.id_control.input.activity_textarea.error.required");
    if (data.description?.length > 160)
      return t("flow.id_control.input.activity_textarea.error.max_length");
    if (data.description?.length < 20)
      return t("flow.id_control.input.activity_textarea.error.min_length");
  };

  return (
    <>
      <Column>
        <div className="mb-12 grow">
          <FlowHeader
            title={t("flow.id_control.signup.declarative_identity.label.four_title")}
            description={t("flow.id_control.signup.declarative_identity.label.four_description")}
            onPreviousClick={() => dispatch(setIsInPreviousStep(true))}
          />
          <AnimatedHeight>
            <Dropdown
              value={data.CSPcode?.toString() ?? null}
              label={`${t("flow.id_control.input.csp_label")} *`}
              placeholder={t("flow.id_control.input.csp_placeholder")}
              items={(CSPCodes?.cspCodes ?? []).map((code) => ({
                key: code.Code.toString(),
                value: code.defaultName
              }))}
              search={true}
              className={"mb-6"}
              onChange={({ key }) => {
                setData((prev) => ({
                  CSPcode: !_.isNaN(parseInt(key)) ? parseInt(key) : null,
                  activity: isCSPCodeNoOccupation(parseInt(key)) ? null : prev.activity,
                  description: isCSPCodeNoOccupation(parseInt(key)) ? null : prev.description
                }));
              }}
            />
            {isActivityVisible && (
              <Dropdown
                value={data.activity ?? null}
                label={t("flow.id_control.input.activity_label")}
                search={true}
                placeholder={t("flow.id_control.input.activity_placeholder")}
                items={(activities?.activities ?? []).map((activity) => ({
                  key: activity.Code.toString(),
                  value: activity.defaultName
                }))}
                onChange={({ key }) => setData((data) => ({ ...data, activity: key }))}
              />
            )}

            {isActivitySelected && (
              <div className={"mt-6"}>
                <InputLabel
                  placeholder={t("flow.id_control.input.activity_textarea.placeholder")}
                  value={data.description ?? undefined}
                  onChange={(value) =>
                    setData((prev) => ({
                      ...prev,
                      description: value.length === 0 ? null : value
                    }))
                  }
                  label={t("flow.id_control.input.activity_textarea.label")}
                  required={true}
                  rows={4}
                  minLength={20}
                  maxLength={160}
                  error={activityDescriptionError()}
                />
              </div>
            )}
          </AnimatedHeight>
        </div>
        <div className="flex shrink-0 flex-col items-center">
          <DotsNavigation total={8} current={5} />
          <Button
            shortcut="enter"
            onClick={execute}
            icon={<ChevronRightIcon />}
            isSubmit={true}
            className="mt-4"
            disabled={isContinueDisabled}
          >
            {t("flow.id_control.input.continue")}
          </Button>
        </div>
      </Column>
    </>
  );
}

export default DeclarativeIdentityOccupation;
